import styled from '@emotion/styled'
import type { Entity as WorksheetEntity } from '@mathflat/domain/@entities/(Content)/Worksheet/dto'
import type { Entity as StudentWorksheetEntity } from '@mathflat/domain/@entities/StudentWorksheet/dto'
import { observer } from 'mobx-react'
import { isTablet } from 'react-device-detect'

import Button from '~/@common/ui/(Button)/Button/Button'
import type { ProblemScoringColl } from '~/@pages/@common/(ProblemScoring)/@trait/ProblemScoring.trait'
import type { ProblemScoringViewOption } from '~/@pages/@common/(ProblemScoring)/@trait/ProblemScoringViewOption.trait'
import type { ProblemScoringFooterProps } from '~/@pages/@common/(ProblemScoring)/ProblemScoringCard/ProblemScoringFooter'

import { HandwrittenNoteInfoTooltip } from '../../Tooltip/HandwrittenNoteInfoTooltip'
import { WorksheetScoringByOneBodyMobile } from './(Body)/WorksheetScoringByOneBody.mobile'
import { WorksheetScoringByOneSwiperController } from './(Header)/WorksheetScoringByOneSwiperController'

type NotFetched = undefined

type Props<T extends '출제' | '미출제'> = {
  studentWorksheet: (T extends '출제' ? StudentWorksheetEntity.StudentWorksheet : null) | NotFetched
  problemScoringColl: ProblemScoringColl<'WORKSHEET'> | NotFetched
  viewOption: ProblemScoringViewOption<'학습모듈' | 'NOT_학습모듈'> | NotFetched
  type: WorksheetEntity.Worksheet['type'] | NotFetched
  isLastScreen?: boolean
  problemIndex?: number
  onProblemIndexChange?: (index: number) => void
  onSubmit?: () => void
  onLocalUserInputChange?: ProblemScoringFooterProps['onLocalUserInputChange']
  onSubmitAnswerImmediately?: ProblemScoringFooterProps['onSubmitAnswerImmediately']
}

const WorksheetScoringByOneMobile = observer(<T extends '출제' | '미출제'>(props: Props<T>) => {
  if (props.problemScoringColl?.toArr.length === 0) {
    return (
      <S.Container>
        <S.EmptyView>
          <img
            src={`/images/icons/icon_description_paper_circle.svg`}
            width={50}
            height={50}
            alt="쌍둥이유사문제없음"
          />
          <p>매칭된 쌍둥이・유사문제가 없어요.</p>
          <p>다음 문제를 풀어보세요.</p>
        </S.EmptyView>
        <Button onClick={props.onSubmit} size="large" theme="primary" type="submit">
          {props.isLastScreen ? '제출하기' : '다음'}
        </Button>
      </S.Container>
    )
  }

  if (!props.problemScoringColl || !props.viewOption || !props.type) {
    return <S.Container />
  }

  return (
    <S.Container>
      <WorksheetScoringByOneSwiperController
        type={props.type}
        totalProblemCount={props.problemScoringColl.toArr.length}
      />
      <WorksheetScoringByOneBodyMobile {...props} />
      {isTablet && <HandwrittenNoteInfoTooltip className="hand-written-info-tooltip" />}
    </S.Container>
  )
})

export default WorksheetScoringByOneMobile

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100%;

    .hand-written-info-tooltip {
      left: 36px;
      bottom: 200px;
      width: calc(100% - 72px);
    }
  `,
  EmptyView: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    flex-direction: column;
    position: relative;
    background-color: white;
    border-radius: var(--Radius-300);
    > img {
      margin-bottom: 15px;
    }
    > button {
      position: absolute;
      bottom: 20px;
      right: 20px;
    }
  `,
}
